/**
 * Zapier-inspired app picker modal for trigger/action step configuration.
 * Left category nav + search + dual-column Home (top apps | built-in utilities).
 */
import React, { useMemo, useState, useEffect } from 'react';
import {
  Dialog,
  Box,
  Typography,
  TextField,
  InputAdornment,
  IconButton,
  Avatar,
  CircularProgress,
} from '@material-ui/core';
import { makeStyles, fade } from '@material-ui/core/styles';
import SearchIcon from '@material-ui/icons/Search';
import CloseIcon from '@material-ui/icons/Close';
import HomeOutlinedIcon from '@material-ui/icons/HomeOutlined';
import AppsOutlinedIcon from '@material-ui/icons/AppsOutlined';
import BuildOutlinedIcon from '@material-ui/icons/BuildOutlined';
import CategoryOutlinedIcon from '@material-ui/icons/CategoryOutlined';
import ArrowBackIcon from '@material-ui/icons/ArrowBack';
import { useSelector } from 'react-redux';
import { selectAllActions, selectAllTriggers, selectApps } from '../../features/allApps/allAppsSlice';
import { getRecentApps, isUtilityApp } from '../../utils/recentApps';
import { BUILT_IN_TRIGGER_PICKER_APPS } from '../../constants/builtInTriggerApps';
import { mergeActionAppsWithBuiltIns } from '../../constants/builtInActionApps';

const NAV = {
  HOME: 'home',
  APPS: 'apps',
  CATEGORIES: 'categories',
  UTILITIES: 'utilities',
};

const useStyles = makeStyles((theme) => {
  const isDark = theme.palette.type === 'dark';
  const sidebarBg = isDark ? theme.palette.grey[900] : '#f7f6f3';
  const hoverBg = isDark ? fade(theme.palette.primary.main, 0.16) : fade('#0069ea', 0.08);
  const activeNavBg = isDark ? fade(theme.palette.primary.main, 0.2) : '#ebe8e1';

  return {
    dialogPaper: {
      borderRadius: 12,
      maxHeight: '88vh',
      height: 640,
      width: '100%',
      maxWidth: 820,
      overflow: 'hidden',
      display: 'flex',
      flexDirection: 'column',
    },
    shell: {
      display: 'flex',
      flex: 1,
      minHeight: 0,
      overflow: 'hidden',
    },
    sidebar: {
      width: 180,
      flexShrink: 0,
      backgroundColor: sidebarBg,
      borderRight: `1px solid ${theme.palette.divider}`,
      padding: theme.spacing(1.5, 1),
      display: 'flex',
      flexDirection: 'column',
      gap: 2,
    },
    navItem: {
      display: 'flex',
      alignItems: 'center',
      gap: theme.spacing(1),
      width: '100%',
      border: 'none',
      background: 'transparent',
      borderRadius: 8,
      padding: theme.spacing(1, 1.25),
      cursor: 'pointer',
      font: 'inherit',
      color: theme.palette.text.primary,
      textAlign: 'left',
      '&:hover': {
        backgroundColor: isDark ? theme.palette.action.hover : '#ebe8e1',
      },
    },
    navItemActive: {
      backgroundColor: activeNavBg,
      fontWeight: 600,
      '& $navIcon': {
        color: theme.palette.primary.main,
      },
    },
    navIcon: {
      fontSize: 20,
      color: theme.palette.text.secondary,
    },
    navLabel: {
      fontSize: '0.875rem',
      fontWeight: 'inherit',
    },
    main: {
      flex: 1,
      minWidth: 0,
      display: 'flex',
      flexDirection: 'column',
      backgroundColor: theme.palette.background.paper,
      position: 'relative',
    },
    closeBtn: {
      position: 'absolute',
      top: 8,
      right: 8,
      zIndex: 2,
    },
    searchRow: {
      padding: theme.spacing(2.5, 3, 1.5),
      paddingRight: theme.spacing(6),
    },
    searchField: {
      '& .MuiOutlinedInput-root': {
        borderRadius: 10,
        backgroundColor: isDark ? theme.palette.action.hover : '#fafafa',
      },
    },
    body: {
      flex: 1,
      minHeight: 0,
      overflowY: 'auto',
      padding: theme.spacing(1, 3, 3),
    },
    homeGrid: {
      display: 'grid',
      gridTemplateColumns: '1fr 1fr',
      gap: theme.spacing(4),
      [theme.breakpoints.down('sm')]: {
        gridTemplateColumns: '1fr',
        gap: theme.spacing(3),
      },
    },
    column: {
      minWidth: 0,
    },
    sectionTitle: {
      fontSize: '0.95rem',
      fontWeight: 700,
      color: theme.palette.text.primary,
      marginBottom: theme.spacing(1),
    },
    list: {
      display: 'flex',
      flexDirection: 'column',
      gap: 2,
    },
    appRow: {
      display: 'flex',
      alignItems: 'center',
      gap: theme.spacing(1.25),
      width: '100%',
      border: 'none',
      background: 'transparent',
      borderRadius: 8,
      padding: theme.spacing(0.85, 1),
      cursor: 'pointer',
      font: 'inherit',
      color: theme.palette.text.primary,
      textAlign: 'left',
      transition: 'background-color 0.12s',
      '&:hover': {
        backgroundColor: hoverBg,
      },
    },
    appIcon: {
      width: 28,
      height: 28,
      flexShrink: 0,
      borderRadius: 6,
      backgroundColor: theme.palette.background.paper,
    },
    appName: {
      fontSize: '0.9rem',
      fontWeight: 500,
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
    },
    empty: {
      padding: theme.spacing(3, 1),
      color: theme.palette.text.secondary,
      fontSize: '0.875rem',
    },
    loadingWrap: {
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      flex: 1,
      minHeight: 200,
    },
    allAppsList: {
      display: 'flex',
      flexDirection: 'column',
      gap: 2,
      maxWidth: 420,
    },
    categoryGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))',
      gap: theme.spacing(1.25),
    },
    categoryCard: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'flex-start',
      gap: theme.spacing(1),
      border: `1px solid ${theme.palette.divider}`,
      borderRadius: 10,
      backgroundColor: theme.palette.background.paper,
      padding: theme.spacing(1.5),
      cursor: 'pointer',
      font: 'inherit',
      color: theme.palette.text.primary,
      textAlign: 'left',
      minHeight: 96,
      transition: 'border-color 0.12s, box-shadow 0.12s, background-color 0.12s',
      '&:hover': {
        borderColor: theme.palette.primary.main,
        backgroundColor: hoverBg,
        boxShadow: `0 0 0 1px ${theme.palette.primary.main}`,
      },
    },
    categoryIcons: {
      display: 'flex',
      alignItems: 'center',
      '& > *': {
        marginRight: -6,
        border: `2px solid ${theme.palette.background.paper}`,
      },
    },
    categoryName: {
      fontSize: '0.85rem',
      fontWeight: 600,
      lineHeight: 1.3,
      overflow: 'hidden',
      display: '-webkit-box',
      WebkitLineClamp: 2,
      WebkitBoxOrient: 'vertical',
    },
    categoryCount: {
      fontSize: '0.75rem',
      color: theme.palette.text.secondary,
    },
    breadcrumbRow: {
      display: 'flex',
      alignItems: 'center',
      gap: theme.spacing(1),
      marginBottom: theme.spacing(1.5),
    },
    backBtn: {
      display: 'inline-flex',
      alignItems: 'center',
      gap: 4,
      border: 'none',
      background: 'transparent',
      color: theme.palette.primary.main,
      cursor: 'pointer',
      font: 'inherit',
      fontSize: '0.875rem',
      fontWeight: 600,
      padding: theme.spacing(0.5, 0.75),
      borderRadius: 6,
      '&:hover': {
        backgroundColor: hoverBg,
      },
    },
  };
});

function matchesQuery(app, query) {
  if (!query) return true;
  const q = query.toLowerCase().trim();
  if (!q) return true;
  const qCompact = q.replace(/\s+/g, '');
  const fields = [app.name, app.app, app.category, app.group]
    .filter(Boolean)
    .map((v) => String(v).toLowerCase());
  return fields.some((f) => f.includes(q) || f.replace(/\s+/g, '').includes(qCompact));
}

function AppRow({ app, onSelect, classes }) {
  return (
    <button type="button" className={classes.appRow} onClick={() => onSelect(app)}>
      <Avatar src={app.icon} alt="" className={classes.appIcon} variant="rounded">
        {(app.name || 'A').charAt(0).toUpperCase()}
      </Avatar>
      <Typography className={classes.appName}>{app.name}</Typography>
    </button>
  );
}

/**
 * @param {object} props
 * @param {boolean} props.open
 * @param {() => void} props.onClose
 * @param {(app: object) => void} props.onSelect
 * @param {'action'|'trigger'} props.mode
 */
const AppPickerModal = ({ open, onClose, onSelect, mode = 'action' }) => {
  const classes = useStyles();
  const allApps = useSelector(selectApps) || [];
  const actionApps = useSelector(selectAllActions) || [];
  const triggerApps = useSelector(selectAllTriggers) || [];
  const [query, setQuery] = useState('');
  const [nav, setNav] = useState(NAV.HOME);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [recentTick, setRecentTick] = useState(0);

  useEffect(() => {
    if (open) {
      setQuery('');
      setNav(NAV.HOME);
      setSelectedCategory(null);
      setRecentTick(Date.now());
    }
  }, [open]);

  const handleNavChange = (id) => {
    setNav(id);
    setSelectedCategory(null);
  };

  const catalogApps = useMemo(() => {
    if (mode === 'trigger') return triggerApps.length ? triggerApps : allApps.filter((a) => a.trigger);
    return actionApps.length ? actionApps : allApps.filter((a) => a.action);
  }, [mode, triggerApps, actionApps, allApps]);

  const builtInUtilities = useMemo(() => {
    if (mode === 'trigger') {
      return BUILT_IN_TRIGGER_PICKER_APPS.map((builtin) => {
        const fromCatalog = catalogApps.find((a) => a.app === builtin.app);
        return fromCatalog ? { ...builtin, ...fromCatalog } : builtin;
      });
    }
    // Ensure Workload Code and other built-in Utilities always appear for actions.
    return mergeActionAppsWithBuiltIns(catalogApps.filter(isUtilityApp)).filter(isUtilityApp);
  }, [mode, catalogApps]);

  const utilitySlugs = useMemo(
    () => new Set(builtInUtilities.map((a) => a.app)),
    [builtInUtilities]
  );

  const thirdPartyApps = useMemo(
    () =>
      catalogApps
        .filter((a) => !utilitySlugs.has(a.app))
        .slice()
        .sort((a, b) => String(a.name || '').localeCompare(String(b.name || ''))),
    [catalogApps, utilitySlugs]
  );

  const recentApps = useMemo(() => {
    const stored = getRecentApps(mode);
    return stored
      .map((storedApp) => {
        const match = catalogApps.find(
          (a) => a.app === storedApp.app || a.name === storedApp.name
        );
        return match || storedApp;
      })
      .filter(Boolean)
      .slice(0, 12);
  }, [mode, catalogApps, recentTick]);

  // "Your top apps" on Home (browse mode only — search uses global results)
  const topApps = useMemo(() => {
    if (recentApps.length > 0) return recentApps;
    return thirdPartyApps.slice(0, 12);
  }, [recentApps, thirdPartyApps]);

  // Categories for the current mode (exclude Utility — covered by Built-in Utilities nav)
  const categories = useMemo(() => {
    const map = new Map();
    catalogApps.forEach((app) => {
      const raw = (app.category || '').trim();
      if (!raw || raw === 'Utility') return;
      if (!map.has(raw)) {
        map.set(raw, { name: raw, count: 0, icons: [] });
      }
      const entry = map.get(raw);
      entry.count += 1;
      if (entry.icons.length < 3 && app.icon) {
        entry.icons.push(app.icon);
      }
    });
    return Array.from(map.values()).sort((a, b) => a.name.localeCompare(b.name));
  }, [catalogApps]);

  const filteredUtilities = useMemo(
    () => builtInUtilities.filter((a) => matchesQuery(a, query)),
    [builtInUtilities, query]
  );

  const filteredThirdParty = useMemo(
    () => thirdPartyApps.filter((a) => matchesQuery(a, query)),
    [thirdPartyApps, query]
  );

  const filteredCategories = useMemo(() => {
    if (!query) return categories;
    const q = query.toLowerCase();
    return categories.filter((c) => c.name.toLowerCase().includes(q));
  }, [categories, query]);

  // Global search across the full catalog for the current mode (independent of active nav tab)
  const isSearching = Boolean(query && query.trim());
  const globalSearchResults = useMemo(() => {
    if (!isSearching) return { apps: [], utilities: [], categories: [] };
    const apps = thirdPartyApps.filter((a) => matchesQuery(a, query));
    const utilities = builtInUtilities.filter((a) => matchesQuery(a, query));
    const q = query.toLowerCase().trim();
    const matchedCategories = categories.filter((c) => c.name.toLowerCase().includes(q));
    return { apps, utilities, categories: matchedCategories };
  }, [isSearching, query, thirdPartyApps, builtInUtilities, categories]);

  const appsInSelectedCategory = useMemo(() => {
    if (!selectedCategory) return [];
    return catalogApps
      .filter((a) => (a.category || '').trim() === selectedCategory)
      .filter((a) => matchesQuery(a, query))
      .slice()
      .sort((a, b) => String(a.name || '').localeCompare(String(b.name || '')));
  }, [catalogApps, selectedCategory, query]);

  const loading = allApps.length === 0;

  const handleSelect = (app) => {
    onSelect(app);
    onClose();
  };

  const searchPlaceholder =
    mode === 'trigger' ? 'Search trigger apps and tools…' : 'Search apps and tools…';

  const topAppsTitle = recentApps.length > 0 ? 'Your top apps' : 'Popular apps';

  const navItems = [
    { id: NAV.HOME, label: 'Home', Icon: HomeOutlinedIcon },
    { id: NAV.APPS, label: 'Apps', Icon: AppsOutlinedIcon },
    { id: NAV.CATEGORIES, label: 'Categories', Icon: CategoryOutlinedIcon },
    { id: NAV.UTILITIES, label: 'Built-in Utilities', Icon: BuildOutlinedIcon },
  ];

  const renderHome = () => (
    <Box className={classes.homeGrid}>
      <Box className={classes.column}>
        <Typography className={classes.sectionTitle}>{topAppsTitle}</Typography>
        {topApps.length === 0 ? (
          <Typography className={classes.empty}>
            No apps yet — browse Apps to get started
          </Typography>
        ) : (
          <Box className={classes.list}>
            {topApps.map((app) => (
              <AppRow
                key={`top-${app.app}-${app.name}`}
                app={app}
                onSelect={handleSelect}
                classes={classes}
              />
            ))}
          </Box>
        )}
      </Box>
      <Box className={classes.column}>
        <Typography className={classes.sectionTitle}>Built-in Utilities</Typography>
        {builtInUtilities.length === 0 ? (
          <Typography className={classes.empty}>No built-in utilities available</Typography>
        ) : (
          <Box className={classes.list}>
            {builtInUtilities.map((app) => (
              <AppRow
                key={`util-${app.app}-${app.name}`}
                app={app}
                onSelect={handleSelect}
                classes={classes}
              />
            ))}
          </Box>
        )}
      </Box>
    </Box>
  );

  const renderSearchResults = () => {
    const { apps, utilities, categories: matchedCats } = globalSearchResults;
    const total = apps.length + utilities.length;
    return (
      <Box>
        <Typography className={classes.sectionTitle} style={{ marginBottom: 12 }}>
          {total === 0
            ? `No results for "${query.trim()}"`
            : `${total} result${total === 1 ? '' : 's'} for "${query.trim()}"`}
        </Typography>
        {matchedCats.length > 0 && (
          <Box style={{ marginBottom: 20 }}>
            <Typography className={classes.sectionTitle} style={{ fontSize: '0.85rem', color: '#666' }}>
              Categories
            </Typography>
            <Box className={classes.categoryGrid} style={{ marginTop: 8 }}>
              {matchedCats.map((cat) => (
                <button
                  key={`search-cat-${cat.name}`}
                  type="button"
                  className={classes.categoryCard}
                  onClick={() => {
                    setNav(NAV.CATEGORIES);
                    setSelectedCategory(cat.name);
                    setQuery('');
                  }}
                >
                  <Box className={classes.categoryIcons}>
                    {cat.icons.slice(0, 3).map((icon, i) => (
                      <Avatar
                        key={`${cat.name}-s-${i}`}
                        src={icon}
                        alt=""
                        style={{ width: 28, height: 28 }}
                        variant="rounded"
                      />
                    ))}
                  </Box>
                  <Typography className={classes.categoryName}>{cat.name}</Typography>
                  <Typography className={classes.categoryCount}>
                    {cat.count} {cat.count === 1 ? 'app' : 'apps'}
                  </Typography>
                </button>
              ))}
            </Box>
          </Box>
        )}
        {utilities.length > 0 && (
          <Box style={{ marginBottom: 20 }}>
            <Typography className={classes.sectionTitle} style={{ fontSize: '0.85rem', color: '#666' }}>
              Built-in Utilities
            </Typography>
            <Box className={classes.allAppsList} style={{ marginTop: 4, maxWidth: '100%' }}>
              {utilities.map((app) => (
                <AppRow
                  key={`search-util-${app.app}-${app.name}`}
                  app={app}
                  onSelect={handleSelect}
                  classes={classes}
                />
              ))}
            </Box>
          </Box>
        )}
        {apps.length > 0 && (
          <Box>
            <Typography className={classes.sectionTitle} style={{ fontSize: '0.85rem', color: '#666' }}>
              Apps
            </Typography>
            <Box className={classes.allAppsList} style={{ marginTop: 4, maxWidth: '100%' }}>
              {apps.map((app) => (
                <AppRow
                  key={`search-app-${app.app}-${app.name}`}
                  app={app}
                  onSelect={handleSelect}
                  classes={classes}
                />
              ))}
            </Box>
          </Box>
        )}
      </Box>
    );
  };

  const renderApps = () => (
    <Box>
      <Typography className={classes.sectionTitle}>Apps</Typography>
      {filteredThirdParty.length === 0 ? (
        <Typography className={classes.empty}>
          {query ? `No apps match "${query}"` : 'No apps available'}
        </Typography>
      ) : (
        <Box className={classes.allAppsList}>
          {filteredThirdParty.map((app) => (
            <AppRow
              key={`app-${app.app}-${app.name}`}
              app={app}
              onSelect={handleSelect}
              classes={classes}
            />
          ))}
        </Box>
      )}
    </Box>
  );

  const renderCategories = () => {
    if (selectedCategory) {
      return (
        <Box>
          <Box className={classes.breadcrumbRow}>
            <button
              type="button"
              className={classes.backBtn}
              onClick={() => setSelectedCategory(null)}
            >
              <ArrowBackIcon style={{ fontSize: 18 }} />
              All categories
            </button>
          </Box>
          <Typography className={classes.sectionTitle}>{selectedCategory}</Typography>
          {appsInSelectedCategory.length === 0 ? (
            <Typography className={classes.empty}>
              {query ? `No apps match "${query}"` : 'No apps in this category'}
            </Typography>
          ) : (
            <Box className={classes.allAppsList}>
              {appsInSelectedCategory.map((app) => (
                <AppRow
                  key={`cat-app-${app.app}-${app.name}`}
                  app={app}
                  onSelect={handleSelect}
                  classes={classes}
                />
              ))}
            </Box>
          )}
        </Box>
      );
    }

    return (
      <Box>
        <Typography className={classes.sectionTitle}>Categories</Typography>
        {filteredCategories.length === 0 ? (
          <Typography className={classes.empty}>
            {query ? `No categories match "${query}"` : 'No categories available'}
          </Typography>
        ) : (
          <Box className={classes.categoryGrid}>
            {filteredCategories.map((cat) => (
              <button
                key={cat.name}
                type="button"
                className={classes.categoryCard}
                onClick={() => setSelectedCategory(cat.name)}
              >
                <Box className={classes.categoryIcons}>
                  {cat.icons.length > 0 ? (
                    cat.icons.map((icon, i) => (
                      <Avatar
                        key={`${cat.name}-icon-${i}`}
                        src={icon}
                        alt=""
                        style={{ width: 28, height: 28 }}
                        variant="rounded"
                      />
                    ))
                  ) : (
                    <Avatar style={{ width: 28, height: 28 }} variant="rounded">
                      <CategoryOutlinedIcon style={{ fontSize: 16 }} />
                    </Avatar>
                  )}
                </Box>
                <Typography className={classes.categoryName}>{cat.name}</Typography>
                <Typography className={classes.categoryCount}>
                  {cat.count} {cat.count === 1 ? 'app' : 'apps'}
                </Typography>
              </button>
            ))}
          </Box>
        )}
      </Box>
    );
  };

  const renderUtilities = () => (
    <Box>
      <Typography className={classes.sectionTitle}>Built-in Utilities</Typography>
      {filteredUtilities.length === 0 ? (
        <Typography className={classes.empty}>
          {query ? `No utilities match "${query}"` : 'No built-in utilities available'}
        </Typography>
      ) : (
        <Box className={classes.allAppsList}>
          {filteredUtilities.map((app) => (
            <AppRow
              key={`util-tab-${app.app}-${app.name}`}
              app={app}
              onSelect={handleSelect}
              classes={classes}
            />
          ))}
        </Box>
      )}
    </Box>
  );

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={false}
      PaperProps={{ className: classes.dialogPaper }}
      aria-label={mode === 'trigger' ? 'Choose a trigger app' : 'Choose an action app'}
    >
      <Box className={classes.shell}>
        <Box className={classes.sidebar} component="nav" aria-label="App categories">
          {navItems.map(({ id, label, Icon }) => (
            <button
              key={id}
              type="button"
              className={`${classes.navItem}${nav === id ? ` ${classes.navItemActive}` : ''}`}
              onClick={() => handleNavChange(id)}
            >
              <Icon className={classes.navIcon} />
              <span className={classes.navLabel}>{label}</span>
            </button>
          ))}
        </Box>

        <Box className={classes.main}>
          <IconButton className={classes.closeBtn} size="small" onClick={onClose} aria-label="Close">
            <CloseIcon fontSize="small" />
          </IconButton>

          <Box className={classes.searchRow}>
            <TextField
              className={classes.searchField}
              fullWidth
              variant="outlined"
              size="small"
              autoFocus
              placeholder={searchPlaceholder}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon color="primary" fontSize="small" />
                  </InputAdornment>
                ),
              }}
            />
          </Box>

          {loading ? (
            <Box className={classes.loadingWrap}>
              <CircularProgress size={36} />
            </Box>
          ) : (
            <Box className={classes.body}>
              {isSearching ? (
                renderSearchResults()
              ) : (
                <>
                  {nav === NAV.HOME && renderHome()}
                  {nav === NAV.APPS && renderApps()}
                  {nav === NAV.CATEGORIES && renderCategories()}
                  {nav === NAV.UTILITIES && renderUtilities()}
                </>
              )}
            </Box>
          )}
        </Box>
      </Box>
    </Dialog>
  );
};

export default AppPickerModal;
